/**
 * Skeleton Table Component
 * Loading placeholder for data tables (alerts, products, history)
 */

import { Card, Box, SkeletonBodyText, BlockStack } from '@shopify/polaris';

interface SkeletonTableProps {
  /**
   * Number of rows to display
   * @default 5
   */
  rows?: number;

  /**
   * Number of columns to display
   * @default 4
   */
  columns?: number;

  /**
   * Whether to show column headers
   * @default true
   */
  showHeaders?: boolean;

  /**
   * Whether to show a title above the table
   * @default false
   */
  showTitle?: boolean;
}

/**
 * Skeleton placeholder for IndexTable-like layouts
 * Simulates table structure with header and rows
 */
export function SkeletonTable({
  rows = 5,
  columns = 4,
  showHeaders = true,
  showTitle = false,
}: SkeletonTableProps) {
  // Column widths for visual variety (percentages)
  const columnWidths = ['25%', '35%', '20%', '20%'];

  return (
    <Card>
      <div className="skeleton-shimmer">
        <BlockStack gap="400">
          {showTitle && (
            <Box paddingBlockEnd="200">
              <div style={{ width: '150px' }}>
                <SkeletonBodyText lines={1} />
              </div>
            </Box>
          )}

          {/* Table container */}
          <Box>
            {/* Header row */}
            {showHeaders && (
              <Box
                paddingBlock="300"
                paddingInline="400"
                background="bg-surface-secondary"
                borderBlockEndWidth="025"
                borderColor="border"
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: columnWidths.slice(0, columns).join(' '),
                    gap: 'var(--p-space-400)',
                  }}
                >
                  {Array.from({ length: columns }).map((_, colIndex) => (
                    <div key={colIndex} style={{ width: '60%' }}>
                      <SkeletonBodyText lines={1} />
                    </div>
                  ))}
                </div>
              </Box>
            )}

            {/* Data rows */}
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <Box
                key={rowIndex}
                paddingBlock="300"
                paddingInline="400"
                borderBlockEndWidth="025"
                borderColor="border"
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: columnWidths.slice(0, columns).join(' '),
                    gap: 'var(--p-space-400)',
                  }}
                >
                  {Array.from({ length: columns }).map((_, colIndex) => (
                    <div
                      key={colIndex}
                      style={{
                        // Vary widths for visual interest
                        width: colIndex === 1 ? '90%' : colIndex === 0 ? '70%' : '50%',
                      }}
                    >
                      <SkeletonBodyText lines={1} />
                    </div>
                  ))}
                </div>
              </Box>
            ))}
          </Box>
        </BlockStack>
      </div>
    </Card>
  );
}

export type { SkeletonTableProps };
